import React from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { LiquidationMap } from "@/shared/types/chart-risk";
import { formatCompactUsd, formatUsd } from "@/shared/utils/format";

import { LINE_CHART_MARGIN_PARAMS_PX } from "../../constants";
import { ChartWrapper } from "../../styled/chart-wrapper";
import { Tooltip as TooltipStyled, TooltipRow } from "../../styled/tooltip";
import { getDisplayData } from "./get-display-data";
import { LiquidationsDisplay, Param } from "./types";

const labelByParam: Record<Param, string> = {
  [Param.ShortCumulativeAmount]: "cumulative shorts",
  [Param.LongCumulativeAmount]: "cumulative longs",
  [Param.ShortAmount]: "shorts",
  [Param.LongAmount]: "longs",
};

const POSITIVE_COLOR = "var(--fill-positive)";
const NEGATIVE_COLOR = "var(--fill-negative)";
interface Props {
  data?: LiquidationMap;
}

export function LiquidationMapChart({ data }: Props) {
  const displayData = getDisplayData(data);
  return (
    <ChartWrapper>
      <ResponsiveContainer>
        <ComposedChart
          data={displayData}
          margin={{ ...LINE_CHART_MARGIN_PARAMS_PX, left: 0 }}
        >
          <YAxis
            tickLine={false}
            yAxisId="left"
            orientation="left"
            tickFormatter={formatCompactUsd}
          />
          <XAxis dataKey="price" tickFormatter={formatUsd} />
          <CartesianGrid strokeDasharray="3 3" />
          <YAxis
            yAxisId="right"
            orientation="right"
            tickFormatter={formatCompactUsd}
          />

          <Tooltip
            content={(value) => {
              const payload = value?.payload?.[0]
                ?.payload as LiquidationsDisplay;
              const isShort = Boolean(payload?.side);
              const amountLabel =
                labelByParam[isShort ? Param.ShortAmount : Param.LongAmount];
              const cumulativeAmountLabel =
                labelByParam[
                  isShort
                    ? Param.ShortCumulativeAmount
                    : Param.LongCumulativeAmount
                ];
              return (
                <TooltipStyled>
                  <TooltipRow>{formatUsd(payload?.price)}</TooltipRow>
                  <TooltipRow color={isShort ? NEGATIVE_COLOR : POSITIVE_COLOR}>
                    {amountLabel}: {formatCompactUsd(payload?.amount)}
                  </TooltipRow>
                  <TooltipRow color={isShort ? NEGATIVE_COLOR : POSITIVE_COLOR}>
                    {cumulativeAmountLabel}: :{" "}
                    {formatCompactUsd(payload?.cumulativeAmount)}
                  </TooltipRow>
                </TooltipStyled>
              );
            }}
          />
          <Bar yAxisId="left" dataKey="shortAmount" fill={NEGATIVE_COLOR} />
          <Bar yAxisId="left" dataKey="longAmount" fill={POSITIVE_COLOR} />
          <Line
            type="monotone"
            dataKey="shortCumulativeAmount"
            yAxisId="right"
            stroke={NEGATIVE_COLOR}
            dot={<span />}
          />
          <Line
            type="monotone"
            dataKey="longCumulativeAmount"
            yAxisId="right"
            stroke={POSITIVE_COLOR}
            dot={<span />}
          />
          <Legend formatter={(value: Param) => labelByParam[value]} />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
}
